/**
 * The docling backend: its state, and the OCR settings a run sends it.
 *
 * WHY THESE SETTINGS LIVE HERE AND NOT IN THE OPTIONS PANEL. They are not
 * OpenDataLoader flags. Upstream's hybrid backend takes them as PROCESS
 * START-UP arguments — one running server, one OCR configuration — so asking
 * "what does force-OCR change on this document" normally means restarting the
 * server between the two runs. The service encodes them into the backend URL
 * instead, one cached converter per distinct settings map, which is what makes
 * them per-run controls at all. Keeping them in their own panel says plainly
 * that this half of the pipeline is docling, not OpenDataLoader.
 */

import { FiPlay, FiSquare } from "react-icons/fi";
import { Button, Card, Checkbox, Input, PillBadge, Select, Spinner } from "@/components/ui";
import { BADGE_TONE } from "@/components/ui/pillTones";
import type { EnginePart } from "@/lib/pdflab";

const PHASE_TONE = {
  ready: BADGE_TONE.green,
  starting: BADGE_TONE.amber,
  off: BADGE_TONE.neutral,
  error: BADGE_TONE.red,
} as const;

/**
 * Engines docling can be asked for. Only EasyOCR's models are baked into the
 * image; the others are listed because the backend accepts them, and choosing
 * one that is not installed fails loudly at converter build rather than
 * silently producing worse text.
 */
const OCR_ENGINES = ["easyocr", "tesseract", "tesserocr", "rapidocr", "auto"];

export interface HybridSettings {
  force_ocr?: boolean;
  no_ocr?: boolean;
  ocr_engine?: string;
  ocr_lang?: string;
  psm?: string;
  enrich_formula?: boolean;
  enrich_picture_description?: boolean;
  picture_description_prompt?: string;
}

export function HybridPanel({
  parts,
  settings,
  enabled,
  busy,
  onSettings,
  onStart,
  onStop,
}: {
  parts: EnginePart[];
  settings: HybridSettings;
  /** True when the run's `hybrid` option is set — the settings are inert otherwise. */
  enabled: boolean;
  busy: boolean;
  onSettings: (next: HybridSettings) => void;
  onStart: () => void;
  onStop: () => void;
}) {
  const hybrid = parts.find((p) => p.id === "hybrid");
  const set = <K extends keyof HybridSettings>(k: K, v: HybridSettings[K]) =>
    onSettings({ ...settings, [k]: v });

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-fh-border-soft px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-fh-body font-medium text-fh-text-strong">Hybrid backend</h3>
            {hybrid && (
              <PillBadge
                value={hybrid.phase}
                tone={PHASE_TONE[hybrid.phase] ?? BADGE_TONE.neutral}
              />
            )}
            {busy && <Spinner className="h-4 w-4" />}
          </div>
          <p className="mt-1 max-w-prose text-fh-meta leading-relaxed text-fh-text-muted">
            {hybrid?.detail ?? "Checking…"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={onStart}
            disabled={busy || hybrid?.phase === "ready" || hybrid?.phase === "starting"}
          >
            <FiPlay className="mr-1.5" /> Start
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onStop}
            disabled={busy || hybrid?.phase === "off"}
          >
            <FiSquare className="mr-1.5" /> Stop
          </Button>
        </div>
      </div>

      <div className={enabled ? "space-y-4 px-4 py-4" : "space-y-4 px-4 py-4 opacity-50"}>
        {!enabled && (
          <p className="text-fh-meta leading-relaxed text-fh-text-muted">
            These apply only when <span className="font-medium">hybrid</span> is set to
            {" "}
            <span className="font-mono">docling-fast</span> in the Hybrid options group.
          </p>
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            label="Force OCR on every page"
            checked={Boolean(settings.force_ocr)}
            disabled={!enabled || Boolean(settings.no_ocr)}
            onChange={(e) => set("force_ocr", e.target.checked)}
          />
          <Checkbox
            label="Disable OCR entirely"
            checked={Boolean(settings.no_ocr)}
            disabled={!enabled || Boolean(settings.force_ocr)}
            onChange={(e) => set("no_ocr", e.target.checked)}
          />
        </div>
        <p className="text-fh-meta leading-relaxed text-fh-text-muted">
          Mutually exclusive, and the pair worth understanding. Default is OCR only where the
          page has no text of its own. <span className="font-medium">Force</span> re-reads even
          pages that carry embedded text — the setting for a scan whose text layer is wrong.
          <span className="font-medium"> Disable</span> is for the opposite case: a clean digital
          PDF, where OCR-ing the charts and screenshots duplicates text that was already extracted.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="OCR engine"
            value={settings.ocr_engine ?? "easyocr"}
            disabled={!enabled}
            onChange={(e) => set("ocr_engine", e.target.value)}
            options={OCR_ENGINES.map((v) => ({ value: v, label: v }))}
            hint="Only easyocr's weights are baked into the image"
          />
          <Input
            label="OCR languages"
            value={settings.ocr_lang ?? ""}
            disabled={!enabled}
            onChange={(e) => set("ocr_lang", e.target.value || undefined)}
            placeholder="en"
            hint="Codes differ per engine: EasyOCR 'ko,en', Tesseract 'kor,eng'"
          />
        </div>

        <Input
          label="Tesseract PSM"
          value={settings.psm ?? ""}
          disabled={!enabled || !String(settings.ocr_engine ?? "").startsWith("tesser")}
          onChange={(e) => set("psm", e.target.value || undefined)}
          placeholder="engine default"
          hint="Page segmentation mode. Ignored unless the engine is a Tesseract one."
        />

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            label="Formula enrichment (LaTeX)"
            checked={Boolean(settings.enrich_formula)}
            disabled={!enabled}
            onChange={(e) => set("enrich_formula", e.target.checked)}
          />
          <Checkbox
            label="Picture description (alt text)"
            checked={Boolean(settings.enrich_picture_description)}
            disabled={!enabled}
            onChange={(e) => set("enrich_picture_description", e.target.checked)}
          />
        </div>

        {settings.enrich_picture_description && (
          <Input
            label="Picture description prompt"
            value={settings.picture_description_prompt ?? ""}
            disabled={!enabled}
            onChange={(e) => set("picture_description_prompt", e.target.value || undefined)}
            placeholder="docling's default prompt"
            hint="Runs SmolVLM-256M over every picture, so it is the slowest option here"
          />
        )}
      </div>
    </Card>
  );
}
